import { useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  Phone,
  Mail,
  Search,
  Menu,
  X,
} from "lucide-react";
import logo from "../assets/logo.webp";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { getAlternateUrl } from "../utils/langRoutes";

function NavbarFr() {
  const [showSearch, setShowSearch] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const navigate = useNavigate();
  const { pathname } = useLocation();
  const alternateUrl = getAlternateUrl(pathname);

  const container = "max-w-[1380px] mx-auto px-4 sm:px-6";

  const resourceLinks = [
    { label: "Blogue", to: "/blog" },
    { label: "FAQ", to: "/fr/faq" },
    { label: "Avis", to: "/fr/avis" },
    { label: "Études de cas", to: "/fr/etudes-de-cas" },
  ];

  const teamLinks = [
    { label: "Aman", to: "/fr/notre-equipe/aman" },
    { label: "Sunaina", to: "/fr/notre-equipe/sunaina" },
    { label: "Shikha", to: "/fr/notre-equipe/shikha" },
    { label: "Gurkiran", to: "/fr/notre-equipe/gurkiran" },
    { label: "Samridhi", to: "/fr/notre-equipe/samridhi" },
    { label: "Aarti", to: "/fr/notre-equipe/aarti" },
    { label: "Ekta", to: "/fr/notre-equipe/ekta" },
    { label: "Yashraj", to: "/fr/notre-equipe/yashraj" },
  ];

  const searchRoutes = {
    accueil: "/fr",
    "nos services": "/fr/tout-ce-que-nous-vendons",
    "assurance vie": "/fr/assurance-vie",
    "assurance vie temporaire": "/fr/assurance-vie-temporaire",
    "assurance vie permanente": "/fr/assurance-vie-permanente",
    "assurance vie entiere": "/fr/assurance-vie-entiere",
    "assurance vie universelle": "/fr/assurance-vie-universelle",
    "assurance hypothecaire": "/fr/assurance-hypothecaire",
    "assurance sans examen": "/fr/assurance-sans-examen-medical",
    "assurance enfants": "/fr/assurance-enfants",
    "assurance maladie": "/fr/assurance-maladie",
    "assurance maladies graves": "/fr/assurance-maladies-graves",
    "assurance invalidite": "/fr/assurance-invalidite",
    "assurance visiteurs": "/fr/assurance-visiteurs-canada",
    "assurance super visa": "/fr/assurance-super-visa",
    "assurance nouveaux arrivants": "/fr/assurance-nouveaux-arrivants",
    "assurance etudiants": "/fr/assurance-etudiants-internationaux",
    "assurance voyage": "/fr/assurance-voyage",
    fhsa: "/fr/compte-epargne-premier-logement",
    resp: "/fr/regime-epargne-etudes",
    tfsa: "/fr/compte-epargne-libre-impot",
    rrsp: "/fr/regime-epargne-retraite",
    rdsp: "/fr/regime-epargne-invalidite",
    blogue: "/blog",
    faq: "/fr/faq",
    avis: "/fr/avis",
    contact: "/fr/contactez-nous",
    "notre equipe": "/fr/notre-equipe",
    aman: "/fr/notre-equipe/aman",
    sunaina: "/fr/notre-equipe/sunaina",
    shikha: "/fr/notre-equipe/shikha",
    gurkiran: "/fr/notre-equipe/gurkiran",
    samridhi: "/fr/notre-equipe/samridhi",
    aarti: "/fr/notre-equipe/aarti",
    "devis vie": "https://compulife.org/website/655/quoter.html",
    "devis visiteurs": "/fr/devis/vtc",
  };

  const handleSearchKeyDown = (e) => {
    if (e.key !== "Enter") return;
    const term = searchTerm.trim().toLowerCase();
    if (!term) return;

    const matchKey = Object.keys(searchRoutes).find((key) => {
      const k = key.toLowerCase();
      return k === term || k.startsWith(term) || k.includes(term) || term.startsWith(k) || term.includes(k);
    });

    if (matchKey) {
      const target = searchRoutes[matchKey];
      if (target.startsWith("http")) {
        window.location.href = target;
      } else {
        navigate(target);
      }
    } else {
      navigate(`/blog/${encodeURIComponent(term)}`);
    }

    setSearchTerm("");
    setShowSearch(false);
    setMobileOpen(false);
  };

  const langSwitcherDesktop = (
    <div className="flex items-center gap-1 text-[14px] font-semibold tracking-widest">
      <Link to={alternateUrl} className="text-white/80 hover:text-white transition">EN</Link>
      <span className="text-white/25">|</span>
      <span className="text-white">FR</span>
    </div>
  );

  const langSwitcherMobile = (
    <div className="flex items-center gap-1 text-[13px] font-semibold tracking-widest px-2 py-1.5 rounded bg-white/10">
      <Link to={alternateUrl} onClick={() => setMobileOpen(false)} className="text-white/80 hover:text-white transition">EN</Link>
      <span className="text-white/30">|</span>
      <span className="text-white">FR</span>
    </div>
  );

  return (
    <nav className="fixed top-0 left-0 right-0 w-full z-[999] text-white bg-[#49628B] shadow-[0_10px_30px_rgba(0,0,0,0.18)]">
      {/* Top bar */}
      <div className={container}>
        <div className="flex flex-wrap justify-center md:justify-end items-center gap-x-4 gap-y-1 text-sm py-2 tracking-wide">
          <a href="tel:+18444400128" className="flex items-center gap-1 hover:text-sky-200 text-white/90">
            <Phone size={15} className="text-sky-200" />
            <span className="text-[15px]">(844) 440-0128</span>
          </a>
          <span className="hidden md:inline mx-4 text-white/25">|</span>
          <a href="mailto:info@einsured.ca" className="flex items-center gap-1 hover:text-sky-200 text-white/90">
            <Mail size={15} className="text-sky-200" />
            <span className="text-[15px]">info@einsured.ca</span>
          </a>
          <span className="hidden md:inline mx-4 text-white/25">|</span>
          {langSwitcherDesktop}
        </div>
      </div>

      <div className={container}>
        <div className="flex items-center justify-between py-3 md:py-4">
          {/* Logo */}
          <Link to="/fr" className="flex items-center space-x-2">
            <img src={logo} alt="logo einsured.ca" width={160} height={118} className="h-12 md:h-14 w-auto" />
          </Link>

          {/* Desktop menu */}
          <ul className="hidden md:flex items-center space-x-8 lg:space-x-10 font-medium text-[16px]">
            {/* Accueil */}
            <li className="relative group cursor-pointer hover:text-white">
              <div className="flex items-center gap-1">
                <Link to="/fr" className="font-semibold text-white hover:text-sky-200 transition">
                  Accueil
                </Link>
              </div>
              <div className="absolute left-0 top-full bg-[#3f567b] rounded shadow-lg w-56 py-2 hidden group-hover:block z-50 text-[15px]">
                <Link to="/fr/tout-ce-que-nous-vendons" className="block px-4 py-2 hover:bg-[#2f4668]">
                  Tout ce que nous vendons
                </Link>
              </div>
            </li>

            {/* Assurance */}
            <li className="relative group cursor-pointer hover:text-white">
              <div className="flex items-center gap-1">
                <span className="font-semibold text-white">Assurance</span>
                <ChevronDown size={16} />
              </div>
              <div className="absolute left-0 top-full bg-[#3f567b] rounded shadow-lg w-64 py-2 hidden group-hover:block z-50 text-[15px]">
                {/* Vie */}
                <div className="relative group/life flex justify-between px-4 py-2 hover:bg-[#2f4668] cursor-pointer">
                  <span>Vie</span>
                  <ChevronRight size={14} />
                  <div className="absolute left-full top-0 bg-[#3f567b] rounded shadow-lg w-72 py-2 hidden group-hover/life:block z-50">
                    <Link to="/fr/assurance-vie" className="block px-4 py-2 hover:bg-[#2f4668]">Assurance vie</Link>
                    <Link to="/fr/assurance-vie-temporaire" className="block px-4 py-2 hover:bg-[#2f4668]">Assurance vie temporaire</Link>
                    {/* Permanente submenu */}
                    <div className="relative group/permanent flex justify-between px-4 py-2 hover:bg-[#2f4668] cursor-pointer">
                      <Link to="/fr/assurance-vie-permanente" className="flex justify-between items-center w-full">
                        <span>Assurance vie permanente</span>
                        <ChevronRight size={14} />
                      </Link>
                      <div className="absolute left-full top-0 bg-[#3f567b] rounded shadow-lg w-72 py-2 hidden group-hover/permanent:block z-50">
                        <Link to="/fr/assurance-vie-entiere" className="block px-4 py-2 hover:bg-[#2f4668]">Assurance vie entière</Link>
                        <Link to="/fr/assurance-vie-universelle" className="block px-4 py-2 hover:bg-[#2f4668]">Assurance vie universelle</Link>
                        <Link to="/fr/assurance-vie-permanente-participante" className="block px-4 py-2 hover:bg-[#2f4668]">Assurance vie permanente avec participation</Link>
                      </div>
                    </div>
                    <Link to="/fr/assurance-hypothecaire" className="block px-4 py-2 hover:bg-[#2f4668]">Assurance hypothécaire</Link>
                    <Link to="/fr/assurance-sans-examen-medical" className="block px-4 py-2 hover:bg-[#2f4668]">Assurance sans examen médical</Link>
                    <Link to="/fr/assurance-enfants" className="block px-4 py-2 hover:bg-[#2f4668]">Assurance enfants</Link>
                    <Link to="/fr/assurance-maladie" className="block px-4 py-2 hover:bg-[#2f4668]">Assurance maladie</Link>
                    <Link to="/fr/assurance-nouveaux-arrivants" className="block px-4 py-2 hover:bg-[#2f4668]">Assurance nouveaux arrivants</Link>
                  </div>
                </div>

                {/* Voyage */}
                <div className="relative group/travel flex justify-between px-4 py-2 hover:bg-[#2f4668] cursor-pointer">
                  <span>Voyage</span>
                  <ChevronRight size={14} />
                  <div className="absolute left-full top-0 bg-[#3f567b] rounded shadow-lg w-64 py-2 hidden group-hover/travel:block z-50">
                    <Link to="/fr/assurance-voyage" className="block px-4 py-2 hover:bg-[#2f4668]">Assurance voyage</Link>
                    <Link to="/fr/assurance-visiteurs-canada" className="block px-4 py-2 hover:bg-[#2f4668]">Assurance visiteurs au Canada</Link>
                    <Link to="/fr/assurance-super-visa" className="block px-4 py-2 hover:bg-[#2f4668]">Assurance super visa</Link>
                    <Link to="/fr/assurance-etudiants-internationaux" className="block px-4 py-2 hover:bg-[#2f4668]">Assurance étudiant international</Link>
                  </div>
                </div>

                {/* Prestations du vivant */}
                <div className="relative group/living flex justify-between px-4 py-2 hover:bg-[#2f4668] cursor-pointer">
                  <span>Prestations du vivant</span>
                  <ChevronRight size={14} />
                  <div className="absolute left-full top-0 bg-[#3f567b] rounded shadow-lg w-60 py-2 hidden group-hover/living:block z-50">
                    <Link to="/fr/assurance-maladies-graves" className="block px-4 py-2 hover:bg-[#2f4668]">Assurance maladies graves</Link>
                    <Link to="/fr/assurance-invalidite" className="block px-4 py-2 hover:bg-[#2f4668]">Assurance invalidité</Link>
                  </div>
                </div>
              </div>
            </li>

            {/* Investissements */}
            <li className="relative group cursor-pointer hover:text-white">
              <div className="flex items-center gap-1">
                <span className="font-semibold text-white">Investissements</span>
                <ChevronDown size={16} />
              </div>
              <div className="absolute left-0 top-full bg-[#3f567b] rounded shadow-lg w-80 py-2 hidden group-hover:block z-50 text-[15px]">
                <Link to="/fr/compte-epargne-premier-logement" className="block px-4 py-2 hover:bg-[#2f4668]">Compte d'épargne libre d'impôt pour l'achat d'une première propriété</Link>
                <Link to="/fr/regime-epargne-etudes" className="block px-4 py-2 hover:bg-[#2f4668]">Régime enregistré d'épargne-études</Link>
                <Link to="/fr/compte-epargne-libre-impot" className="block px-4 py-2 hover:bg-[#2f4668]">Compte d'épargne libre d'impôt</Link>
                <Link to="/fr/regime-epargne-retraite" className="block px-4 py-2 hover:bg-[#2f4668]">Régimes enregistrés d'épargne-retraite</Link>
                <Link to="/fr/regime-epargne-invalidite" className="block px-4 py-2 hover:bg-[#2f4668]">Régime enregistré d'épargne-invalidité</Link>
              </div>
            </li>

            {/* Ressources */}
            <li className="relative group cursor-pointer hover:text-white">
              <div className="flex items-center gap-1">
                <span className="font-semibold text-white">Ressources</span>
                <ChevronDown size={16} />
              </div>
              <div className="absolute left-0 top-full bg-[#3f567b] rounded shadow-lg w-56 py-2 hidden group-hover:block z-50 text-[15px]">
                {resourceLinks.map((item) => (
                  <Link key={item.to} to={item.to} className="block px-4 py-2 hover:bg-[#2f4668]">
                    {item.label}
                  </Link>
                ))}
              </div>
            </li>

            {/* Contactez-nous */}
            <li className="relative group cursor-pointer hover:text-white">
              <div className="flex items-center gap-1">
                <span className="font-semibold text-white">Contactez-nous</span>
                <ChevronDown size={16} />
              </div>
              <div className="absolute left-0 top-full bg-[#3f567b] rounded shadow-lg w-56 py-2 hidden group-hover:block z-50 text-[15px]">
                <div className="relative group/team px-4 py-2 hover:bg-[#2f4668] cursor-pointer">
                  <Link to="/fr/notre-equipe" className="flex justify-between items-center w-full">
                    <span>Notre équipe</span>
                    <ChevronRight size={14} />
                  </Link>
                  <div className="absolute left-full top-0 bg-[#3f567b] rounded shadow-lg w-48 py-2 hidden group-hover/team:block z-50">
                    {teamLinks.map((member) => (
                      <Link key={member.to} to={member.to} className="block px-4 py-2 hover:bg-[#2f4668]">
                        {member.label}
                      </Link>
                    ))}
                  </div>
                </div>
                <Link to="/fr/contactez-nous" className="block px-4 py-2 hover:bg-[#2f4668]">
                  Contactez-nous
                </Link>
              </div>
            </li>
          </ul>

          {/* Right side (desktop) */}
          <div className="hidden md:flex items-center space-x-4 relative">
            {/* Search */}
            <div className="relative">
              <button
                onClick={() => setShowSearch((prev) => !prev)}
                className="cursor-pointer text-white/90 hover:text-white transition"
                aria-expanded={showSearch}
                aria-label="Rechercher"
              >
                <Search size={20} />
              </button>
              {showSearch && (
                <div className="absolute right-0 top-full mt-1 flex items-center bg-[#3f567b] rounded shadow-lg px-2 py-1 z-50">
                  <input
                    type="text"
                    placeholder="Rechercher..."
                    autoFocus
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    onBlur={() => setTimeout(() => setShowSearch(false), 200)}
                    className="bg-transparent border border-white/40 text-white text-sm px-2 py-1 rounded focus:outline-none w-44"
                  />
                </div>
              )}
            </div>

            {/* Obtenir un devis */}
            <div className="relative group">
              <button className="bg-sky-400 hover:bg-sky-500 text-white font-semibold px-6 py-2 rounded-full inline-flex items-center gap-1 text-[15px] transition">
                Obtenir un devis
                <ChevronDown size={16} />
              </button>
              <div className="absolute right-0 top-full bg-[#3f567b] rounded shadow-lg w-56 py-2 hidden group-hover:block z-50 text-[15px]">
                <a
                  href="https://compulife.org/website/655/quoter.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block px-4 py-2 hover:bg-[#2f4668]"
                >
                  Assurance vie
                </a>
                <Link to="/fr/devis/vtc" className="block px-4 py-2 hover:bg-[#2f4668]">
                  Assurance visiteurs
                </Link>
              </div>
            </div>
          </div>

          {/* Mobile hamburger */}
          <div className="md:hidden flex items-center gap-2">
            <button onClick={() => setMobileOpen(true)} aria-label="Ouvrir le menu" className="p-2">
              <Menu size={24} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Drawer */}
      {mobileOpen && (
        <>
          <div className="fixed inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <div className="fixed top-0 right-0 h-full w-[88%] sm:w-[420px] bg-[#49628B] text-white shadow-xl z-[60] overflow-y-auto">
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
              <img src={logo} alt="logo einsured.ca" width={160} height={118} className="h-10 w-auto" />
              <div className="flex items-center gap-2">
                <a href="tel:+18444400128" className="px-3 py-1.5 rounded bg-white/10 hover:bg-white/15 text-sm">Appeler</a>
                <a href="mailto:info@einsured.ca" className="px-3 py-1.5 rounded bg-white/10 hover:bg-white/15 text-sm">Courriel</a>
                {langSwitcherMobile}
                <button onClick={() => setMobileOpen(false)} aria-label="Fermer le menu" className="p-2 rounded hover:bg-white/10">
                  <X size={22} />
                </button>
              </div>
            </div>

            {/* Search row */}
            <div className="p-4">
              <div className="flex items-center bg-white/10 rounded px-2 py-2">
                <Search size={18} className="mr-2" />
                <input
                  type="text"
                  placeholder="Rechercher..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  className="bg-transparent text-white text-sm focus:outline-none w-full"
                />
              </div>
            </div>

            <nav className="px-2 pb-6 text-[15px] space-y-2">
              {/* Accueil */}
              <details>
                <summary className="list-none px-3 py-3 rounded hover:bg-white/10 flex justify-between items-center">
                  <span className="font-semibold">Accueil</span>
                  <ChevronDown size={18} />
                </summary>
                <div className="pl-4 pb-2">
                  <Link to="/fr" onClick={() => setMobileOpen(false)} className="block px-2 py-2 rounded hover:bg-white/10">Accueil</Link>
                  <Link to="/fr/tout-ce-que-nous-vendons" onClick={() => setMobileOpen(false)} className="block px-2 py-2 rounded hover:bg-white/10">Tout ce que nous vendons</Link>
                </div>
              </details>

              {/* Assurance */}
              <details>
                <summary className="list-none px-3 py-3 rounded hover:bg-white/10 flex justify-between items-center">
                  <span className="font-semibold">Assurance</span>
                  <ChevronDown size={18} />
                </summary>
                <div className="pl-4">
                  {/* Vie */}
                  <details>
                    <summary className="list-none px-2 py-2 rounded hover:bg-white/10 flex justify-between items-center">
                      <span>Vie</span>
                      <ChevronRight size={18} />
                    </summary>
                    <div className="pl-3 pb-2">
                      <Link to="/fr/assurance-vie" onClick={() => setMobileOpen(false)} className="block px-2 py-2 rounded hover:bg-white/10">Assurance vie</Link>
                      <Link to="/fr/assurance-vie-temporaire" onClick={() => setMobileOpen(false)} className="block px-2 py-2 rounded hover:bg-white/10">Assurance vie temporaire</Link>
                      <details>
                        <summary className="list-none px-2 py-2 rounded hover:bg-white/10 flex justify-between items-center">
                          <span>Assurance vie permanente</span>
                          <ChevronRight size={18} />
                        </summary>
                        <div className="pl-3 pb-2">
                          <Link to="/fr/assurance-vie-entiere" onClick={() => setMobileOpen(false)} className="block px-2 py-2 rounded hover:bg-white/10">Assurance vie entière</Link>
                          <Link to="/fr/assurance-vie-universelle" onClick={() => setMobileOpen(false)} className="block px-2 py-2 rounded hover:bg-white/10">Assurance vie universelle</Link>
                          <Link to="/fr/assurance-vie-permanente-participante" onClick={() => setMobileOpen(false)} className="block px-2 py-2 rounded hover:bg-white/10">Assurance vie permanente avec participation</Link>
                        </div>
                      </details>
                      <Link to="/fr/assurance-hypothecaire" onClick={() => setMobileOpen(false)} className="block px-2 py-2 rounded hover:bg-white/10">Assurance hypothécaire</Link>
                      <Link to="/fr/assurance-sans-examen-medical" onClick={() => setMobileOpen(false)} className="block px-2 py-2 rounded hover:bg-white/10">Assurance sans examen médical</Link>
                      <Link to="/fr/assurance-enfants" onClick={() => setMobileOpen(false)} className="block px-2 py-2 rounded hover:bg-white/10">Assurance enfants</Link>
                      <Link to="/fr/assurance-maladie" onClick={() => setMobileOpen(false)} className="block px-2 py-2 rounded hover:bg-white/10">Assurance maladie</Link>
                      <Link to="/fr/assurance-nouveaux-arrivants" onClick={() => setMobileOpen(false)} className="block px-2 py-2 rounded hover:bg-white/10">Assurance nouveaux arrivants</Link>
                    </div>
                  </details>

                  {/* Voyage */}
                  <details>
                    <summary className="list-none px-2 py-2 rounded hover:bg-white/10 flex justify-between items-center">
                      <span>Voyage</span>
                      <ChevronRight size={18} />
                    </summary>
                    <div className="pl-3 pb-2">
                      <Link to="/fr/assurance-voyage" onClick={() => setMobileOpen(false)} className="block px-2 py-2 rounded hover:bg-white/10">Assurance voyage</Link>
                      <Link to="/fr/assurance-visiteurs-canada" onClick={() => setMobileOpen(false)} className="block px-2 py-2 rounded hover:bg-white/10">Assurance visiteurs au Canada</Link>
                      <Link to="/fr/assurance-super-visa" onClick={() => setMobileOpen(false)} className="block px-2 py-2 rounded hover:bg-white/10">Assurance super visa</Link>
                      <Link to="/fr/assurance-etudiants-internationaux" onClick={() => setMobileOpen(false)} className="block px-2 py-2 rounded hover:bg-white/10">Assurance étudiant international</Link>
                    </div>
                  </details>

                  {/* Prestations du vivant */}
                  <details>
                    <summary className="list-none px-2 py-2 rounded hover:bg-white/10 flex justify-between items-center">
                      <span>Prestations du vivant</span>
                      <ChevronRight size={18} />
                    </summary>
                    <div className="pl-3 pb-2">
                      <Link to="/fr/assurance-maladies-graves" onClick={() => setMobileOpen(false)} className="block px-2 py-2 rounded hover:bg-white/10">Assurance maladies graves</Link>
                      <Link to="/fr/assurance-invalidite" onClick={() => setMobileOpen(false)} className="block px-2 py-2 rounded hover:bg-white/10">Assurance invalidité</Link>
                    </div>
                  </details>
                </div>
              </details>

              {/* Investissements */}
              <details>
                <summary className="list-none px-3 py-3 rounded hover:bg-white/10 flex justify-between items-center">
                  <span className="font-semibold">Investissements</span>
                  <ChevronDown size={18} />
                </summary>
                <div className="pl-4 pb-2">
                  <Link to="/fr/compte-epargne-premier-logement" onClick={() => setMobileOpen(false)} className="block px-2 py-2 rounded hover:bg-white/10">Compte d'épargne libre d'impôt pour l'achat d'une première propriété</Link>
                  <Link to="/fr/regime-epargne-etudes" onClick={() => setMobileOpen(false)} className="block px-2 py-2 rounded hover:bg-white/10">Régime enregistré d'épargne-études</Link>
                  <Link to="/fr/compte-epargne-libre-impot" onClick={() => setMobileOpen(false)} className="block px-2 py-2 rounded hover:bg-white/10">Compte d'épargne libre d'impôt</Link>
                  <Link to="/fr/regime-epargne-retraite" onClick={() => setMobileOpen(false)} className="block px-2 py-2 rounded hover:bg-white/10">Régimes enregistrés d'épargne-retraite</Link>
                  <Link to="/fr/regime-epargne-invalidite" onClick={() => setMobileOpen(false)} className="block px-2 py-2 rounded hover:bg-white/10">Régime enregistré d'épargne-invalidité</Link>
                </div>
              </details>

              {/* Ressources */}
              <details>
                <summary className="list-none px-3 py-3 rounded hover:bg-white/10 flex justify-between items-center">
                  <span className="font-semibold">Ressources</span>
                  <ChevronDown size={18} />
                </summary>
                <div className="pl-4 pb-2">
                  {resourceLinks.map((item) => (
                    <Link key={item.to} to={item.to} onClick={() => setMobileOpen(false)} className="block px-2 py-2 rounded hover:bg-white/10">
                      {item.label}
                    </Link>
                  ))}
                </div>
              </details>

              {/* Contactez-nous */}
              <details>
                <summary className="list-none px-3 py-3 rounded hover:bg-white/10 flex justify-between items-center">
                  <span className="font-semibold">Contactez-nous</span>
                  <ChevronDown size={18} />
                </summary>
                <div className="pl-4 pb-2">
                  <details>
                    <summary className="list-none px-2 py-2 rounded hover:bg-white/10 flex justify-between items-center">
                      <span>Notre équipe</span>
                      <ChevronRight size={18} />
                    </summary>
                    <div className="pl-3 pb-2">
                      {teamLinks.map((member) => (
                        <Link key={member.to} to={member.to} onClick={() => setMobileOpen(false)} className="block px-2 py-2 rounded hover:bg-white/10">
                          {member.label}
                        </Link>
                      ))}
                    </div>
                  </details>
                  <Link to="/fr/contactez-nous" onClick={() => setMobileOpen(false)} className="block px-2 py-2 rounded hover:bg-white/10">
                    Contactez-nous
                  </Link>
                </div>
              </details>

              {/* Obtenir un devis */}
              <details>
                <summary className="list-none mx-2 mt-1 bg-sky-400 hover:bg-sky-500 text-sky-900 font-semibold px-4 py-3 rounded-full flex justify-between items-center">
                  <span>Obtenir un devis</span>
                  <ChevronDown size={18} />
                </summary>
                <div className="pl-4 pb-2 mt-2">
                  <a
                    href="https://compulife.org/website/655/quoter.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setMobileOpen(false)}
                    className="block px-2 py-2 rounded hover:bg-white/10"
                  >
                    Assurance vie
                  </a>
                  <Link to="/fr/devis/vtc" onClick={() => setMobileOpen(false)} className="block px-2 py-2 rounded hover:bg-white/10">
                    Assurance visiteurs
                  </Link>
                </div>
              </details>
            </nav>
          </div>
        </>
      )}
    </nav>
  );
}

export default NavbarFr;
